/*
 * Image spec SKUs: a product image can carry a spec SKU (same grammar as
 * `?sku=`) in its own MediaImage metafields. Liquid and the Storefront API
 * cannot read those, so on page load this asks design-store for every image of
 * the product, in the background:
 *
 *   GET {serviceApiUrl}/api/design-store/rest/shopify/products/{productId}/media-images/metafields
 *   Shop-Code: {shopCode}
 *   → { images: [{ id, mediaId, alt, metafields: [{ namespace, key, type, value }] }] }
 *
 * `mediaId` equals Liquid's `media.id`, the tail of each product thumbnail's
 * `data-target` ("{section.id}-{media.id}"). Picking a thumbnail whose image
 * has a SKU puts the calculator on it (`window.pdpApplySpecSku`,
 * main-product.liquid: in place where it can, else a rebuild). Picking one
 * without a SKU only shows it: the calculator keeps whatever is on it — an
 * earlier image's SKU, the shopper's own changes. On a page with bundle options
 * (GB-592) an image SKU belongs to the size its image's bundle code names (the
 * `custom.bundle` metafield: a bundle option's code or handle), or to the
 * product's own size, the default option, when the image has none: picked
 * while another size is showing, the calculator switches to that size first
 * (pdpApplySpecSku). A SKU that size cannot take, or a bundle code naming none
 * of the page's options, is not applied, and the image just shows. An image
 * without a SKU never changes the size, bundle code or not. A page without
 * bundle options ignores bundle codes.
 *
 * A thumbnail click applies at once. A swipe applies the slide it settles on
 * (debounced), not every slide it passes — an apply can rebuild the calculator.
 * Swipes are heard on the theme gallery (`slideChanged`) and on the compactV2
 * mobile calculator's own summary bar, whose slides are the tent hero's
 * `data-calc-slot="summary-images"` list in the same order. On the tent layout
 * (`?cmp=tent`) the hero's image carousel and its lightbox announce the image
 * they settled on themselves (`tent:bundleSelected`). On the compressed page on
 * a phone (`?cmp=1`) the gallery only browses photos: its swipes are ignored.
 */
(function () {
  if (window.__imageSpecSkuInstalled) return;
  window.__imageSpecSkuInstalled = true;

  const script = document.currentScript || document.querySelector('script[data-image-spec-sku]');
  const { shopCode, productId } = (script && script.dataset) || {};
  if (!shopCode || !productId || !window.serviceApiUrl) return;

  /** Metafield keys in the `custom` namespace read as the image's spec SKU, first wins. */
  const SKU_KEYS = ['sku', 'spec_sku', 'default_spec_sku'];
  /** Metafield keys in the `custom` namespace read as the image's bundle code, first wins. */
  const BUNDLE_CODE_KEYS = ['bundle', 'bundle_sku'];

  /**
   * mediaId → `{ sku, bundleCode }` (each null when the image has none); null
   * until the request has answered. Only ids in here are product images —
   * bundle-option, 3D and video thumbnails are not, and are ignored.
   */
  let imageByMediaId = null;
  /** An image picked before the request answered. */
  let pendingMediaId = null;
  let pendingSummaryIndex;
  /** The image SKU on the calculator now; null when the selection is the shopper's own. */
  let imageSku = null;
  /** The bundle code that went with `imageSku`; null for none (the default size). */
  let imageBundleCode = null;
  /**
   * The last image SKU handed to pdpApplySpecSku, with the bookkeeping from
   * before it, for `pdp:specSkuDeclined`.
   */
  let lastRequest = null;
  /** Set by an image SKU's own size switch; read by `applyForMedia` around its call. */
  let switchedForImage = false;

  // A bundle option switch (GB-592) builds a fresh calculator on the picked
  // size: an image SKU applied to the previous one went with it, and the new
  // selection is the shopper's own. Without this, picking that image again
  // would read as "already applied" and do nothing.
  //
  // An image SKU picked on another size switches to its own size instead
  // (pdpApplySpecSku, main-product.liquid, `pdpSwitchingForImageSku`): during
  // `applyForMedia`, or later, once a calculator that was loading has loaded.
  // That image's SKU is the one going on, so it stays `imageSku`; the other
  // size's image SKU went with that size, so a decline goes back to none.
  window.addEventListener('calc:bundleChange', () => {
    if (window.pdpSwitchingForImageSku) {
      switchedForImage = true;
      if (lastRequest) {
        lastRequest.previousImageSku = null;
        lastRequest.previousImageBundleCode = null;
      }
      return;
    }
    imageSku = null;
    imageBundleCode = null;
    lastRequest = null;
  });

  // A SKU handed over while the calculator was still loading is reported as
  // applied and tried once it has loaded (main-product.liquid). When it does
  // not take then, the calculator never changed: go back to the bookkeeping
  // from before that request.
  window.addEventListener('pdp:specSkuDeclined', (event) => {
    if (!lastRequest || !event.detail || event.detail.sku !== lastRequest.sku) return;
    imageSku = lastRequest.previousImageSku;
    imageBundleCode = lastRequest.previousImageBundleCode;
    lastRequest = null;
  });

  // The shopper changing the calculator themselves: the image SKU is no longer
  // what is on it, so picking that image again applies it again. Any touch
  // counts, on the same surfaces as `pdpSuppress3dReveal` (main-product.liquid,
  // which also counts the tent switcher; keep the two in step) — re-applying a
  // SKU already on screen is harmless. The calculator is its container and the
  // theme slots the compressed (`?cmp=1`) and tent (`?cmp=tent`) layouts move
  // its body, total and actions into (`data-calc-slot`). Not its photo slides
  // (the compactV2 summary bar, the `summary-images` list): swiping those is
  // picking images. Nor the theme's own blocks in the compressed `body` slot
  // (title, rating and price; feature and value chips), which the calculator's
  // body is only appended after. Nor the slots the calculator only prints into
  // (the tent price row, the delivery dates). Nor the tent size switcher: its
  // `calc:bundleChange` resets this already. The request before the touch has
  // nothing to go back to any more either: a decline must not bring its image
  // SKU back.
  const CALCULATOR_SURFACES = '#calc-container, [data-calc-slot]';
  const NOT_CONFIGURING = [
    '#compact-v2-summary-slides',
    '[data-calc-slot="summary-images"]',
    '.compressed-product-summary',
    '.compressed-feature-chips',
    '.compressed-value-chips',
    '[data-calc-slot="tent-total"]',
    '[data-calc-slot="delivery-date"]',
    '[data-calc-slot="tent-delivery-date"]',
    '[data-calc-slot="tent-switcher"]',
  ].join(', ');
  ['pointerdown', 'touchstart', 'keydown'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const target = event.target;
        if (!imageSku || !target || !target.closest) return;
        if (!target.closest(CALCULATOR_SURFACES) || target.closest(NOT_CONFIGURING)) return;
        imageSku = null;
        imageBundleCode = null;
        lastRequest = null;
      },
      { capture: true, passive: true }
    );
  });

  /**
   * Metafield types that can hold a SKU or bundle code: plain text (the
   * dashboard writes image fields as `multi_line_text_field`). Every other
   * type's value is a `gid://…` or JSON (references, lists, rich text, money,
   * measurements), never either. A field without a type is read as text.
   */
  const TEXT_TYPES = ['single_line_text_field', 'multi_line_text_field'];
  const isTextType = (type) => !type || TEXT_TYPES.includes(type);

  /**
   * The first of `keys` the image has a non-blank text `custom` metafield for,
   * trimmed. One of another type is skipped with a warning: a type design-store
   * starts sending that is not in TEXT_TYPES must not go dark without a word.
   */
  function readImageField(image, keys) {
    const metafields = Array.isArray(image.metafields) ? image.metafields : [];
    for (const key of keys) {
      const field = metafields.find((metafield) => metafield.namespace === 'custom' && metafield.key === key);
      if (field && !isTextType(field.type)) {
        console.warn(`[image-spec-sku] ignoring custom.${key}: unexpected metafield type`, field.type);
        continue;
      }
      const value = field && typeof field.value === 'string' ? field.value.trim() : '';
      if (value) return value;
    }
    return null;
  }

  /**
   * The rebuilt calculator draws its summary bar on the first slide. Put it
   * back on the one the shopper swiped to, through the channel the bar already
   * follows for the host's lightbox (`calc:imageLightboxClosed`).
   */
  function restoreSummarySlide(index) {
    const calculator = window.pdpCheckout && window.pdpCheckout.calculator;
    if (!index || !calculator || typeof calculator.onContentLoaded !== 'function') return;
    calculator.onContentLoaded((loaded) => {
      if (!loaded) return;
      requestAnimationFrame(() =>
        window.dispatchEvent(new CustomEvent('calc:imageLightboxClosed', { detail: { index } }))
      );
    });
  }

  function applyForMedia(mediaId, summaryIndex) {
    if (!imageByMediaId.has(mediaId)) return;
    const { sku, bundleCode } = imageByMediaId.get(mediaId);
    // An image without a SKU only shows: the calculator keeps what is on it.
    if (!sku) return;
    // Same image SKU again: nothing to rebuild.
    if (sku === imageSku && bundleCode === imageBundleCode) return;
    if (typeof window.pdpApplySpecSku !== 'function') return;

    const previousCheckout = window.pdpCheckout;
    switchedForImage = false;
    // `imageSku`: an image's own SKU may switch the size, to the one its
    // bundle code names (or the default).
    if (!window.pdpApplySpecSku(sku, { imageSku: true, bundleCode: bundleCode })) return;
    // Taken after the call: queueing this request can decline the one queued
    // before it, and switching to the image's size resets it — either way to
    // what the calculator actually has, not to the state before. A switch made
    // during the call left the previous size's image SKU behind with that size:
    // a decline goes back to none, as the `calc:bundleChange` handler does for
    // a switch made later.
    lastRequest = {
      sku: sku,
      previousImageSku: switchedForImage ? null : imageSku,
      previousImageBundleCode: switchedForImage ? null : imageBundleCode,
    };
    imageSku = sku;
    imageBundleCode = bundleCode;
    // Applied in place, the bar never moved; only a rebuilt one starts over.
    if (window.pdpCheckout !== previousCheckout) restoreSummarySlide(summaryIndex);
  }

  fetch(
    `${window.serviceApiUrl}/api/design-store/rest/shopify/products/${encodeURIComponent(productId)}/media-images/metafields`,
    { headers: { 'Shop-Code': shopCode } }
  )
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then((data) => {
      imageByMediaId = new Map();
      (data && Array.isArray(data.images) ? data.images : []).forEach((image) => {
        if (!image.mediaId) return;
        imageByMediaId.set(String(image.mediaId), {
          sku: readImageField(image, SKU_KEYS),
          bundleCode: readImageField(image, BUNDLE_CODE_KEYS),
        });
      });
      if (pendingMediaId) applyForMedia(pendingMediaId, pendingSummaryIndex);
      pendingMediaId = null;
    })
    .catch((error) => {
      console.warn('[image-spec-sku] image metafields request failed; images will not change the specs.', error);
    });

  /**
   * `target` is "{section.id}-{media.id}" (gallery thumbnail / slide) or a bare
   * media id (tent hero slide); `summaryIndex` is the summary bar slide, if any.
   */
  function selectMedia(target = '', summaryIndex) {
    const mediaId = target.slice(target.lastIndexOf('-') + 1);
    if (imageByMediaId) {
      applyForMedia(mediaId, summaryIndex);
    } else {
      pendingMediaId = mediaId;
      pendingSummaryIndex = summaryIndex;
    }
  }

  // Delegated, so it also covers thumbnails added to the gallery after load.
  document.addEventListener('click', (event) => {
    const item = event.target.closest && event.target.closest('[id^="GalleryThumbnails"] [data-target]');
    if (!item || !event.target.closest('button')) return;
    selectMedia(item.dataset.target);
  });

  // `slideChanged` fires on every scroll of the viewer, including the theme's
  // own (a variant change brings its featured slide in, a bundle switch goes
  // back to the first slide), so only a scroll the shopper started on that
  // viewer counts: a pointer down on it arms it, a pointer down anywhere else
  // or the applied swipe disarms it.
  let gestureViewer = null;
  document.addEventListener(
    'pointerdown',
    (event) => {
      gestureViewer = (event.target.closest && event.target.closest('slider-component[id^="GalleryViewer"]')) || null;
    },
    { capture: true, passive: true }
  );

  // The compressed page on a phone (`?cmp=1`, the gate's `calc-compressed`
  // class; 768px, the width its layout switches at — snippets/
  // compressed-gallery.liquid) shows the gallery as a small swipeable square
  // that only browses photos: a swipe there never changes the selection. Its
  // tap-to-zoom lightbox does not either (it announces only on `?cmp=tent`).
  const compressedPhone = window.matchMedia('(max-width: 768px)');
  const browsesOnly = () =>
    compressedPhone.matches && document.documentElement.classList.contains('calc-compressed');

  // `slideChanged` does not bubble, so it is heard on each viewer. It fires for
  // every page a swipe crosses; only the one it settles on is applied.
  const SWIPE_SETTLE_MS = 400;
  document.querySelectorAll('slider-component[id^="GalleryViewer"]').forEach((viewer) => {
    let settleTimer = null;
    viewer.addEventListener('slideChanged', (event) => {
      if (gestureViewer !== viewer || browsesOnly()) return;
      const slide = event.detail && event.detail.currentElement;
      clearTimeout(settleTimer);
      if (!slide || !slide.dataset.mediaId) return;
      settleTimer = setTimeout(() => {
        gestureViewer = null;
        selectMedia(slide.dataset.mediaId);
      }, SWIPE_SETTLE_MS);
    });
  });

  // The tent hero's carousel and lightbox (`?cmp=tent`) announce the image a
  // step or a swipe settled on — already debounced there — by its media id.
  window.addEventListener('tent:bundleSelected', (event) => {
    const mediaId = event.detail && event.detail.mediaId;
    if (mediaId) selectMedia(String(mediaId));
  });

  // The calculator's summary bar is rebuilt with it, so its scroll is heard in
  // the capture phase on the document (element scrolls do not bubble).
  let summaryTimer = null;
  document.addEventListener(
    'scroll',
    (event) => {
      const slides = event.target;
      if (!slides || slides.id !== 'compact-v2-summary-slides') return;
      clearTimeout(summaryTimer);
      summaryTimer = setTimeout(() => {
        if (!slides.isConnected || !slides.clientWidth) return;
        const index = Math.round(slides.scrollLeft / slides.clientWidth);
        const source = document.querySelectorAll('[data-calc-slot="summary-images"] [data-media-id]')[index];
        if (source) selectMedia(source.dataset.mediaId, index);
      }, SWIPE_SETTLE_MS);
    },
    { capture: true, passive: true }
  );
})();
